import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API from "../lib/api";
import { ArrowDownToLine, ArrowUpFromLine, Trophy } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const diff = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch { return ""; }
}

function MiniList({ rows, accent, icon: Icon, title }) {
  return (
    <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 overflow-hidden" data-testid={`landing-proof-${title.toLowerCase().includes("deposit") ? "deposits" : "withdrawals"}`}>
      <div className="px-4 py-3 border-b border-[#2A3241]/40 flex items-center gap-2" style={{ background: `${accent}0A` }}>
        <Icon className="w-4 h-4" style={{ color: accent }} />
        <h3 className="text-xs font-bold tracking-widest uppercase" style={{ color: accent }}>{title}</h3>
      </div>
      <div className="divide-y divide-[#2A3241]/30">
        {rows.length === 0 && <p className="px-4 py-6 text-xs text-[#64748B] text-center">No entries yet.</p>}
        {rows.slice(0, 5).map((r, i) => (
          <div key={i} className="px-4 py-3 flex items-center gap-3 hover:bg-[#1C212E]/40 transition-colors">
            <Trophy className="w-3.5 h-3.5 flex-shrink-0" style={{ color: accent }} />
            <span className="font-medium text-sm text-[#F8FAFC] truncate flex-1">{r.username}</span>
            <span className="font-mono-game text-sm font-bold" style={{ color: accent }}>${(r.amount || 0).toFixed(2)}</span>
            <span className="font-mono-game text-xs text-[#FFD600] w-12 text-right">{r.currency}</span>
            <span className="text-[10px] text-[#64748B] w-14 text-right">{timeAgo(r.created_at)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PaymentProofsLanding() {
  const [data, setData] = useState({ deposits: [], withdrawals: [], settings: {} });

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await API.get("/payment-proofs");
        if (alive) setData(data);
      } catch { /* ignore */ }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const s = data.settings || {};
  const showPreview = !!s.landing_preview_enabled;
  const showCounter = !!s.counter_enabled && (s.counter_amount_label || s.counter_count_label);

  if (!showPreview && !showCounter) return null;

  return (
    <section className="py-16 px-6 bg-[#0B0E14]" data-testid="landing-payment-proofs">
      <div className="max-w-6xl mx-auto">
        {showCounter && (
          <div className="bg-gradient-to-r from-[#10B981]/10 via-[#FFD600]/5 to-[#00F0FF]/10 rounded-2xl border border-[#FFD600]/20 px-8 py-6 mb-10 text-center" data-testid="landing-proof-counter">
            <div className="flex flex-col sm:flex-row gap-6 sm:gap-12 items-center justify-center">
              {s.counter_amount_label && (
                <div>
                  <p className="text-2xl sm:text-3xl font-black font-mono-game text-[#10B981] glow-cyan">{s.counter_amount_label}</p>
                  <p className="text-xs uppercase tracking-widest text-[#94A3B8] mt-1">paid out {s.counter_amount_period || "this month"}</p>
                </div>
              )}
              {s.counter_count_label && (
                <div>
                  <p className="text-2xl sm:text-3xl font-black font-mono-game text-[#FFD600]">{s.counter_count_label}</p>
                  <p className="text-xs uppercase tracking-widest text-[#94A3B8] mt-1">successful withdrawals</p>
                </div>
              )}
            </div>
          </div>
        )}

        {showPreview && (
          <>
            <div className="text-center mb-8">
              <p className="text-xs font-mono-game text-[#10B981] tracking-[0.3em] uppercase mb-2">Trusted by Players</p>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#F8FAFC]">Recent Payouts</h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <MiniList title="Recent Deposits" icon={ArrowDownToLine} rows={data.deposits || []} accent="#10B981" />
              <MiniList title="Recent Withdrawals" icon={ArrowUpFromLine} rows={data.withdrawals || []} accent="#FFD600" />
            </div>
            <div className="text-center mt-6">
              <Link to="/payment-proofs" className="inline-flex items-center gap-2 px-5 py-2.5 text-xs font-bold uppercase tracking-widest border border-[#FFD600]/40 text-[#FFD600] rounded-lg hover:bg-[#FFD600]/10 transition-all" data-testid="landing-view-all-proofs-btn">
                View All Payouts →
              </Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
